import React, { useState, useEffect } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { createPortal } from 'react-dom';
import { TruckIcon } from '@heroicons/react/24/outline';
import { GhostSuggestion } from './GhostMoveCard';
import styles from './GhostAssignModal.module.css';

interface GhostAssignModalProps {
  suggestion: GhostSuggestion;
  driverName: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export const GhostAssignModal: React.FC<GhostAssignModalProps> = ({
  suggestion,
  driverName,
  onConfirm,
  onCancel
}) => {
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isAssigning) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel, isAssigning]);

  const handleConfirm = async () => {
    setIsAssigning(true);
    try {
      await onConfirm();
    } finally {
      setIsAssigning(false);
    }
  };

  const scorePercent = Math.round(suggestion.total_score * 100);

  const formatLocation = (city: string | null, state: string | null) => {
    if (!city && !state) return 'Unknown';
    return [city, state].filter(Boolean).join(', ');
  };

  // Rendered only while open. Alt+C cancels; the assign confirm stays click-only.
  useAltHotkeys({ C: onCancel }, undefined, { layer: true });

  return createPortal(
    <div className={styles.overlay} onClick={onCancel}>
      <div
          className={styles.modal}
          onClick={e => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ghost-modal-title"
        >
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <TruckIcon width={24} height={24} />
          </div>
          <div>
            <h2 id="ghost-modal-title" className={styles.title}>Assign Move to {driverName}?</h2>
            <p className={styles.subtitle}>
              PRO {suggestion.pronumber}-{suggestion.legnum}
            </p>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Move Details</div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Type</span>
            <span className={styles.detailValue}>{suggestion.move_type || 'N/A'}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Origin</span>
            <span className={styles.detailValue}>
              {formatLocation(suggestion.origin_city, suggestion.origin_state)}
            </span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Destination</span>
            <span className={styles.detailValue}>
              {formatLocation(suggestion.dest_city, suggestion.dest_state)}
            </span>
          </div>
          {suggestion.customer_name && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Customer</span>
              <span className={styles.detailValue}>{suggestion.customer_name}</span>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Score Breakdown</div>
          <div className={styles.scoreBreakdown}>
            <div className={styles.scoreStat}>
              <div className={styles.scoreValue}>{scorePercent}%</div>
              <div className={styles.scoreLabel}>Overall Match</div>
            </div>
            <div className={styles.scoreStat}>
              <div className={styles.scoreValue}>{suggestion.deadhead_to_origin_miles.toFixed(0)}</div>
              <div className={styles.scoreLabel}>Miles to Pickup</div>
            </div>
            {suggestion.deadhead_from_dest_miles !== null && (
              <div className={styles.scoreStat}>
                <div className={styles.scoreValue}>{suggestion.deadhead_from_dest_miles.toFixed(0)}</div>
                <div className={styles.scoreLabel}>Miles to Next</div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={onCancel} disabled={isAssigning}>
            Cancel
          </button>
          <button
            className={styles.assignButton}
            onClick={handleConfirm}
            disabled={isAssigning}
          >
            {isAssigning ? 'Assigning...' : 'Assign Move'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GhostAssignModal;
